import axios from 'axios';
import awsConfig from '../config/aws-config';
import awsAuthService from './aws-auth';
import { usageTracker } from './usage-tracker';

class AWSApiService {
    private baseURL = awsConfig.apiGateway.URL;

    /**
     * Get Auth Headers
     */
    private async getAuthHeaders() {
        try {
            const session = await awsAuthService.getSession();
            const token = session.getIdToken().getJwtToken();
            return {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            };
        } catch (error) {
            return {
                'Content-Type': 'application/json',
            };
        }
    }

    /**
     * Generic GET request
     */
    public async get(path: string, params: any = {}): Promise<any> {
        const headers = await this.getAuthHeaders();
        const response = await axios.get(`${this.baseURL}${path}`, { headers, params });
        usageTracker.track('read', 1); // 1 API Call = 1 Unit
        return response.data;
    }

    /**
     * Generic POST request
     */
    public async post(path: string, data: any): Promise<any> {
        const headers = await this.getAuthHeaders();
        const response = await axios.post(`${this.baseURL}${path}`, data, { headers });
        usageTracker.track('write', 1);
        return response.data;
    }

    /**
     * Generic PUT request
     */
    public async put(path: string, data: any): Promise<any> {
        const headers = await this.getAuthHeaders();
        const response = await axios.put(`${this.baseURL}${path}`, data, { headers });
        usageTracker.track('write', 1);
        return response.data;
    }

    /**
     * Generic DELETE request
     */
    public async delete(path: string): Promise<any> {
        const headers = await this.getAuthHeaders();
        const response = await axios.delete(`${this.baseURL}${path}`, { headers });
        usageTracker.track('write', 1);
        return response.data;
    }

    /**
     * Specific Data Methods (to match Firestore patterns)
     */

    public async getInvoices(userId: string): Promise<any[]> {
        return this.get('invoices', { userId });
    }

    public async saveInvoice(invoice: any): Promise<any> {
        if (invoice.id || invoice.invoiceId) {
            return this.put(`invoices/${invoice.id || invoice.invoiceId}`, invoice);
        }
        return this.post('invoices', invoice);
    }

    public async getClients(userId: string): Promise<any[]> {
        return this.get('clients', { userId });
    }

    public async saveClient(client: any): Promise<any> {
        if (client.id || client.clientId) {
            return this.put(`clients/${client.id || client.clientId}`, client);
        }
        return this.post('clients', client);
    }

    public async getInventory(userId: string): Promise<any[]> {
        return this.get('inventory', { userId });
    }

    public async saveInventoryItem(item: any): Promise<any> {
        if (item.id || item.itemId) {
            return this.put(`inventory/${item.id || item.itemId}`, item);
        }
        return this.post('inventory', item);
    }

    public async getBusinessDetails(userId: string): Promise<any> {
        return this.get(`users/${userId}`);
    }

    public async saveBusinessDetails(details: any): Promise<any> {
        return this.put(`users/${details.userId}`, details);
    }
}

const awsApiService = new AWSApiService();
export default awsApiService;
